<template lang="html">
  <div class="crypto-addresses-table__row" :class="rowClass">
    <div class="crypto-addresses-table__row__name">
      <crypto-address-name-inline-editor :address="address" ref="nameEditor"
                                         @update-address="onRenamed"
                                         @start-edit="onStartEdit"></crypto-address-name-inline-editor>
    </div>
    <div class="crypto-addresses-table__row__address">
      <div class="break-words">
        <template v-if="!address.is_generating">{{ address.address }}</template>
        <template v-if="address.is_generating">
          <div class="crypto-addresses-table__row__address__is_generating">
            {{ $t('Идет процесс создания...') }}
          </div>
        </template>
        <template v-if="address.address">
          <BitcoinIncomingInfo :address="address.address" :currency="address.currency" />
        </template>
      </div>
      <div>
        <div class="address-system" :class="addressSystemClass"
             v-if="address.system">{{ address.system }}
        </div>
      </div>
    </div>
    <div class="crypto-addresses-table__row__visibility">
      <template v-if="isToggling">
        <template v-if="!address.hidden"><img class="fast-blinking" src="./invisible.svg" width="16"
                                              height="15.18" alt="Hidden"/></template>
        <template v-else><img class="fast-blinking" src="./visible.svg" width="16" height="10.91"
                              alt="Visible"/></template>
      </template>
      <template v-else><a class="toggle-visibility" href="#" @click.prevent="toggleVisibility">
        <template v-if="address.hidden"><img src="./invisible.svg" width="16" height="15.18"
                                             alt="Hidden"/></template>
        <template v-else><img src="./visible.svg" width="16" height="10.91" alt="Visible"/>
        </template>
      </a></template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {Component, Prop, Watch} from 'vue-property-decorator';
import {
  CryptoAddress,
  CryptoAddressFindModel,
  CryptoAddressVisibilityModel,
} from '@/models/finance/crypto';
import CryptoAddressNameInlineEditor
  from '@/views/pages/finance/accounts/crypto/usdt/CryptoAddressNameInlineEditor.vue';
import BitcoinIncomingInfo from "@/views/pages/finance/accounts/crypto/BitcoinIncomingInfo.vue";

class GeneratingWatcher {
  findModel: CryptoAddressFindModel = new CryptoAddressFindModel();

  generatingTimer: any | null = null;

  address!: CryptoAddress | null;

  limit = 5;

  get isGenerated() {
    return this.address && this.address.is_generating;
  }

  startWatch(address: CryptoAddress) {
    this.address = address;
    if (!this.address.is_generating) return;
    if (this.limit-- < 0) return;

    this.generatingTimer = setTimeout(() => {
      this.findModel.findAddress(this.address!.id).then((address: CryptoAddress) => {
        this.address = address;
        if (this.address.is_generating) this.startWatch(this.address);
      }).catch((err) => {
        this.startWatch(this.address as CryptoAddress);
      });
    }, 1000);
  }

  endWatch() {
    if (this.generatingTimer) clearTimeout(this.generatingTimer);
  }
}

@Component({
  name: 'crypto-address-table-row',
  components: {BitcoinIncomingInfo, CryptoAddressNameInlineEditor},
})
export default class CryptoAddressTableRow extends Vue {
  @Prop({required: true})
  address!: CryptoAddress

  visibilityModel: CryptoAddressVisibilityModel = new CryptoAddressVisibilityModel();

  findModel: CryptoAddressFindModel = new CryptoAddressFindModel();

  gw: GeneratingWatcher = new GeneratingWatcher();

  onStartEdit() {
    this.$emit('start-edit');
  }

  stopEdit() {
    (this.$refs.nameEditor as any).stopEdit();
  }

  mounted() {
    this.gw.startWatch(this.address);
  }

  get isGenerated() {
    return this.gw.isGenerated;
  }

  @Watch('isGenerated')
  onGenerated(newVal) {
    this.$emit('update-address', this.gw.address);
  }

  onRenamed(address: CryptoAddress) {
    this.$emit('update-address', address);
  }

  get addressSystemClass() {
    const system = this.address.system ? (this.address.system as string).toLowerCase() : 'none';

    return {
      'address-system': true,
      [`address-system__${system}`]: true,
    };
  }

  toggleVisibility() {
    this.visibilityModel.toggleVisibility(this.address).then((model) => {
      this.$emit('update-address', CryptoAddress.createFromData(model.options.address));
    });
  }

  get isToggling() {
    return this.visibilityModel.state.isLoading;
  }

  get rowClass() {
    return {
      'crypto-addresses-table__row__hidden': this.address.hidden,
    };
  }
}
</script>

<style scoped lang="less">
.crypto-addresses-table__row {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 145%;
  color: #333333;

  display: contents;

  .break-words {
    white-space: normal;
    word-wrap: break-word;
    word-break: break-all;
  }

  .crypto-addresses-table__row__address {

  }

  &__hidden {
    color: #999999;

    .address-system {
      opacity: 0.5;
    }

    ::v-deep {
      a.editable-link {
        opacity: 0.5;
      }
    }
  }

  .fast-blinking {
    animation: blinker 1s linear infinite;
  }

  &__address {
    display: grid;
    grid-template-columns: auto min-content;
    grid-gap: 20px;

    &__is_generating {
      color: #777777;
      animation: blinker 2s linear infinite;
    }
  }

  .address-system {

    border-radius: 7.5px;
    color: #fff;
    display: inline-block;
    padding: 1px 7px;
    font-size: 10px;
    line-height: 16px;
    text-transform: uppercase;

    margin-left: 20px;

    &__erc20 {
      background: #8392DB;
    }

    &__trc20 {
      background: #E97D75;
    }
  }

  a.toggle-visibility {
    &:hover {
      color: #71a719;
      //img {
      //  fill: #71a719;
      //}
    }
  }
}

@keyframes blinker {
  50% {
    opacity: 0;
  }
}
</style>

<i18n>
{
  "en": {
    "Идет процесс создания...": "The address is creating..."
  }
}
</i18n>
